import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  ChevronDown, 
  ChevronRight, 
  Download, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  Info
} from 'lucide-react';
import { PlatformId } from '../../lib/store/usePostingStore';

interface PlatformResponse {
  platform: PlatformId;
  request: any;
  response: any;
  status: 'pending' | 'success' | 'error';
}

interface DebugModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: () => void;
  platformData: Record<PlatformId, any>;
}

const DebugModal: React.FC<DebugModalProps> = ({ 
  isOpen, 
  onClose, 
  onSend,
  platformData
}) => {
  const [responses, setResponses] = useState<PlatformResponse[]>([]);
  const [expandedPlatforms, setExpandedPlatforms] = useState<PlatformId[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [allCompleted, setAllCompleted] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      const initialResponses = Object.entries(platformData)
        .filter(([_, data]) => data !== null)
        .map(([platform, data]) => ({
          platform: platform as PlatformId,
          request: data,
          response: null,
          status: 'pending' as const
        }));
      
      setResponses(initialResponses);
      setExpandedPlatforms([]);
      setIsSending(false);
      setAllCompleted(false);
    }
  }, [isOpen, platformData]);

  const toggleAccordion = (platform: PlatformId) => {
    setExpandedPlatforms(prev => 
      prev.includes(platform) 
        ? prev.filter(p => p !== platform) 
        : [...prev, platform]
    );
  };

  const handleSend = async () => {
    setIsSending(true);
    
    // Process each platform sequentially
    for (const response of responses) {
      try {
        // Simulate API call with random success/failure
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        const success = Math.random() > 0.3; // 70% success rate for demo
        
        // Update response
        setResponses(prev => 
          prev.map(r => 
            r.platform === response.platform 
              ? { 
                  ...r, 
                  status: success ? 'success' : 'error',
                  response: success 
                    ? { success: true, post_id: `post_${Math.random().toString(36).substring(7)}` }
                    : { error: 'API Error', code: 401, message: 'Invalid OAuth token' }
                }
              : r
          )
        );
        
        // Auto-expand failed platforms
        if (!success) {
          setExpandedPlatforms(prev => 
            prev.includes(response.platform) ? prev : [...prev, response.platform]
          );
        }
      } catch (error) {
        setResponses(prev => 
          prev.map(r => 
            r.platform === response.platform 
              ? { 
                  ...r, 
                  status: 'error',
                  response: { error: 'Exception', message: error instanceof Error ? error.message : 'Unknown error' }
                }
              : r
          )
        );
        
        // Auto-expand on error
        setExpandedPlatforms(prev => 
          prev.includes(response.platform) ? prev : [...prev, response.platform]
        );
      }
    }
    
    setIsSending(false);
    setAllCompleted(true);
  };

  const downloadDebugData = () => {
    const data = {
      timestamp: new Date().toISOString(),
      platforms: responses
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `serge-debug-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getPlatformIcon = (platform: PlatformId) => {
    switch (platform) {
      case 'twitter':
        return <TwitterIcon className="h-5 w-5" style={{ color: '#1DA1F2' }} />;
      case 'facebook':
        return <FacebookIcon className="h-5 w-5" style={{ color: '#1877F2' }} />;
      case 'instagram':
        return <InstagramIcon className="h-5 w-5" style={{ color: '#E4405F' }} />;
      case 'linkedin':
        return <LinkedinIcon className="h-5 w-5" style={{ color: '#0A66C2' }} />;
      case 'youtube':
        return <YoutubeIcon className="h-5 w-5" style={{ color: '#FF0000' }} />;
      case 'tiktok':
        return <TikTokIcon className="h-5 w-5" style={{ color: '#000000' }} />;
      default:
        return <User className="h-5 w-5" />;
    }
  };

  const getStatusBadge = (status: 'pending' | 'success' | 'error') => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-0.5 bg-gray-100 dark:bg-dark-700 text-gray-700 dark:text-gray-300 rounded-full text-xs">READY</span>;
      case 'success':
        return <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded-full text-xs flex items-center"><CheckCircle className="h-3 w-3 mr-1" /> SUCCESS</span>;
      case 'error':
        return <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded-full text-xs flex items-center"><XCircle className="h-3 w-3 mr-1" /> ERROR</span>;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black z-50"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white dark:bg-dark-800 rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-dark-700">
                <div className="flex items-center space-x-2">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Payload Debugger</h2>
                  <div className="flex items-center ml-2 text-amber-500 dark:text-amber-400">
                    <AlertTriangle className="h-4 w-4 mr-1" />
                    <span className="text-xs">Contains sensitive OAuth tokens</span>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-dark-700"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <div className="p-4 overflow-y-auto max-h-[calc(90vh-8rem)]">
                <div className="space-y-4">
                  {responses.map((response) => (
                    <div 
                      key={response.platform}
                      className={`border rounded-lg overflow-hidden ${
                        response.status === 'error' 
                          ? 'border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/10' 
                          : 'border-gray-200 dark:border-dark-700'
                      }`}
                    >
                      <div 
                        className="flex items-center justify-between p-4 cursor-pointer"
                        onClick={() => toggleAccordion(response.platform)}
                      >
                        <div className="flex items-center space-x-3">
                          {getPlatformIcon(response.platform)}
                          <span className="font-medium text-gray-900 dark:text-white">
                            {response.platform === 'fb' ? 'Facebook' : 
                             response.platform === 'ig' ? 'Instagram' : 
                             response.platform === 'yt' ? 'YouTube' : 
                             response.platform === 'ln' ? 'LinkedIn' : 
                             response.platform === 'x' ? 'Twitter (X)' : 
                             response.platform === 'tt' ? 'TikTok (Disabled)' : 
                             response.platform}
                          </span>
                          <div className="ml-2">
                            {getStatusBadge(response.status)}
                          </div>
                        </div>
                        <div>
                          {expandedPlatforms.includes(response.platform) ? (
                            <ChevronDown className="h-5 w-5 text-gray-400" />
                          ) : (
                            <ChevronRight className="h-5 w-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                      
                      {expandedPlatforms.includes(response.platform) && (
                        <div className="p-4 border-t border-gray-200 dark:border-dark-700">
                          <div className="space-y-4">
                            <div>
                              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Request</h3>
                              <pre 
                                className={`text-xs p-3 rounded-lg overflow-auto max-h-60 ${
                                  response.status === 'error' 
                                    ? 'bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800/30' 
                                    : 'bg-gray-50 dark:bg-dark-700'
                                }`}
                              >
                                {JSON.stringify(response.request, null, 2)}
                              </pre>
                            </div>
                            
                            <div>
                              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Response</h3>
                              <pre className="text-xs p-3 rounded-lg overflow-auto max-h-60 bg-gray-50 dark:bg-dark-700">
                                {response.response 
                                  ? JSON.stringify(response.response, null, 2) 
                                  : "Awaiting response..."}
                              </pre>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="p-4 border-t border-gray-200 dark:border-dark-700 flex justify-between">
                <div className="flex items-center text-gray-500 dark:text-gray-400">
                  <Info className="h-4 w-4 mr-2" />
                  <span className="text-xs">For internal testing only</span>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={downloadDebugData}
                    disabled={!allCompleted}
                    className={`px-4 py-2 flex items-center space-x-2 rounded-lg text-sm ${
                      allCompleted
                        ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/30'
                        : 'bg-gray-100 dark:bg-dark-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <Download className="h-4 w-4" />
                    <span>Download as .json</span>
                  </button>
                  
                  {isSending ? (
                    <button
                      disabled
                      className="px-4 py-2 bg-gray-300 dark:bg-dark-700 text-gray-500 dark:text-gray-400 rounded-lg text-sm flex items-center space-x-2"
                    >
                      <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full"></div>
                      <span>Processing...</span>
                    </button>
                  ) : allCompleted ? (
                    <button
                      onClick={onClose}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700"
                    >
                      Close Debugger
                    </button>
                  ) : (
                    <button
                      onClick={handleSend}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700"
                    >
                      Send to Platforms
                    </button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// Platform Icons
const TwitterIcon = (props: any) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
  </svg>
);

const FacebookIcon = (props: any) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const InstagramIcon = (props: any) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z" />
  </svg>
);

const LinkedinIcon = (props: any) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const YoutubeIcon = (props: any) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
  </svg>
);

const TikTokIcon = (props: any) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

const User = (props: any) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

export default DebugModal;